import * as memberImportApi from 'api/memberImport';
import * as registrationImportApi from 'api/meetRegistrationImport';
import { memberImportText } from './memberImportText';

export const IMPORT_TYPES = {
  MEMBER_IMPORT: {
    key: 'MEMBER_IMPORT',
    apiValue: 0,
  },
  REGISTRATION_IMPORT: {
    key: 'REGISTRATION_IMPORT',
    apiValue: 2,
  },
};

// groupingBasis is how the club groups its athletes (utils/groupLabel.js). A club that groups by year
// level is told about the Year Level column and given a template that has it. teamWords is the club's
// words for a team (utils/teamLabel.js) when it has teams, and nothing otherwise: a club with teams is
// told about the Team column and given a template that has it.
export const getImportConfig = (importType, groupingBasis, teamWords) => {
  const configs = {
    [IMPORT_TYPES.MEMBER_IMPORT.key]: {
      title: 'Athlete Import',
      description: 'Import athletes and their primary contacts from a CSV file',
      // The description, the words in bold and the template: memberImportText.js.
      ...memberImportText(groupingBasis, teamWords),
      importCsv: memberImportApi.importCsv,
      previewCsv: memberImportApi.previewCsv,
      applyCsv: memberImportApi.applyCsv,
      getImportStatus: memberImportApi.getImportStatus,
      getImportErrors: memberImportApi.getImportErrors,
      cancelImport: memberImportApi.cancelImport,
      clearImportHistory: memberImportApi.clearImportHistory,
      getLatestImport: memberImportApi.getLatestImport,
      importType: IMPORT_TYPES.MEMBER_IMPORT.key,
      showMergeReplaceOptions: false,
      supportsPreview: true, // dry-run preview (new platform-aware resolution) — member importer only for now
      supportsApply: true, // synchronous Confirm & Apply (replaces the legacy queued Start Import for members)
    },
    [IMPORT_TYPES.REGISTRATION_IMPORT.key]: {
      title: 'Registration Import',
      description: 'Import meet registration data from CSV file',
      fileDescription:
        'Select a CSV file of meet registrations to import. Columns are matched by their header name, so they can be in any order, and any extra columns are ignored. Required columns: [First Name], [Last Name], [Date of Birth], [Gender], [Bib Number], [Events], [Primary Contact First Name], [Primary Contact Last Name], and [Primary Contact Email]. Optional column: [Club Name] (the club a visitor represents). [Events] is a single cell of event names separated by semicolons (for example "100m; 200m; Long Jump"), and each name must match one of your club\'s event types. [Date of Birth] must use a four-digit year (for example 2015-01-07) — the importer auto-detects the format ([YYYY-MM-DD] or [DD/MM/YYYY]) and shows it in the preview. [Gender] accepts M, F, Male, or Female. [Bib Number] must be greater than 0 and not conflict with existing numbers already in use in the club.',
      boldPatterns: [
        '[First Name]',
        '[Last Name]',
        '[Date of Birth]',
        '[Gender]',
        '[Bib Number]',
        '[Events]',
        '[Club Name]',
        '[Primary Contact First Name]',
        '[Primary Contact Last Name]',
        '[Primary Contact Email]',
        '[YYYY-MM-DD]',
        '[DD/MM/YYYY]',
      ],
      templates: [
        {
          name: 'CSV file template',
          url: '/templates/registration-import-template-splitfast.csv',
        },
      ],
      importCsv: registrationImportApi.importCsv,
      previewCsv: registrationImportApi.previewCsv,
      applyCsv: registrationImportApi.applyCsv,
      getImportStatus: memberImportApi.getImportStatus,
      getImportErrors: memberImportApi.getImportErrors,
      cancelImport: memberImportApi.cancelImport,
      clearImportHistory: memberImportApi.clearImportHistory,
      getLatestImport: memberImportApi.getLatestImport,
      importType: IMPORT_TYPES.REGISTRATION_IMPORT.key,
      showMergeReplaceOptions: false, // merge-only on the new sync path (no Replace, no removals)
      supportsPreview: true, // dry-run preview (platform-aware resolution)
      supportsApply: true, // synchronous Confirm & Apply (replaces the legacy queued Start Import)
    },
  };

  return configs[importType] || configs[IMPORT_TYPES.MEMBER_IMPORT.key];
};

export const getImportTypeFromPath = (pathname) => {
  if (pathname.includes('/meet-registration')) {
    return IMPORT_TYPES.REGISTRATION_IMPORT.key;
  }
  if (pathname.includes('/members/import')) {
    return IMPORT_TYPES.MEMBER_IMPORT.key;
  }
  return IMPORT_TYPES.MEMBER_IMPORT.key;
};

export const getApiValue = (importTypeKey) => {
  return IMPORT_TYPES[importTypeKey]?.apiValue || 0;
};
